/**
 * DemoStaged — a pre-built, ZERO-AI demo board.
 *
 * Route: /demo/staged (see App.js). Additive: does not touch /demo or
 * DemoBoardPage.jsx.
 *
 * Flow (spec 2026-07-08):
 *   1. Card asks "Would you like help building your first board?"
 *   2. Tap "Yes, build it for me" → 1.6s "building…" animation
 *   3. Pre-built 24-tile board renders using real images pre-fetched at
 *      build time into /public/demo/ (Openverse photos + ARASAAC pictos).
 *      Tap a tile → speak via existing tts.speak().
 *
 * Hard rules (from the spec):
 *   - NO LLM/AI calls anywhere.
 *   - NO runtime API calls (no Openverse/ARASAAC fetches). Images ship
 *     with the build. Must be instant and never fail under traffic.
 *   - Reuse existing tile + tts components — don't build new speech infra.
 *   - Real photos for concrete tiles; ARASAAC pictos for abstract core
 *     words (yes/no/more/stop/help/please). No brand logos, no scraped
 *     images.
 *
 * Attribution: rendered inline on each tile card (small footer line) +
 * a single "credits" toggle at the bottom that lists every source. The
 * source manifest lives at /public/demo/attribution.json alongside the
 * images (fetched with a plain <fetch> on mount — same-origin static
 * file, no external network).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Volume2, ArrowRight } from "lucide-react";
import { speak, cancelSpeech, isSupported as ttsSupported } from "@/lib/tts";

// Category palette — matches the tokenized brand colors used elsewhere.
// (Colors are HEX only inside this constant because they're paired
// per-category. Not a JSX hard-code — index.css exposes brand tokens.)
const CATEGORY_META = {
  core:       { label: "Core",       color: "#FFB627" },
  food:       { label: "Food",       color: "#FF6B6B" },
  feelings:   { label: "Feelings",   color: "#C39BD3" },
  people:     { label: "People",     color: "#4D96FF" },
  activities: { label: "Activities", color: "#6BCB77" },
};

const CATEGORY_ORDER = ["core", "food", "feelings", "people", "activities"];

export default function DemoStaged({ embedded = false } = {}) {
  const [step, setStep] = useState("offer"); // offer | building | board
  const [tiles, setTiles] = useState([]);
  const [pressedSlug, setPressedSlug] = useState(null);
  const [showCredits, setShowCredits] = useState(false);
  const [manifestError, setManifestError] = useState(false);

  // Preload the manifest as soon as the page mounts so the "building"
  // animation doesn't hide a slow static-file read on cold cache.
  useEffect(() => {
    fetch("/demo/attribution.json", { cache: "force-cache" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((m) => setTiles(Array.isArray(m.tiles) ? m.tiles : []))
      .catch(() => setManifestError(true));
    return () => cancelSpeech();
  }, []);

  const startBuild = () => {
    setStep("building");
    // Deliberate 1.6s pause — "for the feel of it" (spec §2). Not tied to
    // any real work; the board is already loaded.
    setTimeout(() => setStep("board"), 1600);
  };

  const onTileTap = (tile) => {
    if (!ttsSupported()) return;
    setPressedSlug(tile.slug);
    setTimeout(() => setPressedSlug(null), 220);
    cancelSpeech();
    speak(tile.speak_text || tile.label, { rate: 0.95, pitch: 1.05 });
  };

  // Group tiles by category for rendering
  const grouped = CATEGORY_ORDER.map((cat) => ({
    key: cat,
    meta: CATEGORY_META[cat],
    tiles: tiles.filter((t) => t.category === cat),
  })).filter((g) => g.tiles.length > 0);

  // -------------------- OFFER SCREEN --------------------
  if (step === "offer") {
    return (
      <div
        className={
          embedded
            ? "flex items-center justify-center px-4 py-8"
            : "min-h-screen bg-[color:var(--paper)] flex items-center justify-center px-4 py-10"
        }
        data-testid="demo-staged-page"
      >
        <div className="brutal-card bg-[color:var(--cream)] p-6 sm:p-8 max-w-md w-full text-center">
          <div className="mx-auto w-14 h-14 rounded-xl border-[3px] border-[color:var(--ink)] bg-[color:var(--gold)] flex items-center justify-center mb-4">
            <Sparkles size={26} />
          </div>
          <h1 className="display text-2xl sm:text-3xl font-extrabold text-[color:var(--ink)] leading-tight mb-2">
            Would you like help building your first board?
          </h1>
          <p className="text-sm text-[color:var(--ink-soft)] mb-6 leading-relaxed">
            We'll set up a starter board with core words, food, feelings,
            people, and activities. You can change everything later.
          </p>
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={startBuild}
              className="pill-btn primary justify-center text-base"
              data-testid="demo-staged-yes"
              disabled={manifestError}
            >
              Yes, build it for me <ArrowRight size={16} />
            </button>
            <Link
              to="/create-board"
              className="pill-btn justify-center text-sm"
              data-testid="demo-staged-self"
            >
              I'll do it myself
            </Link>
          </div>
          {manifestError && (
            <div className="text-xs text-[color:var(--coral-deep)] mt-4">
              Demo board couldn't load. Please refresh.
            </div>
          )}
        </div>
      </div>
    );
  }

  // -------------------- BUILDING ANIMATION --------------------
  if (step === "building") {
    return (
      <div
        className={
          embedded
            ? "flex flex-col items-center justify-center px-4 py-24"
            : "min-h-screen bg-[color:var(--paper)] flex flex-col items-center justify-center px-4"
        }
        data-testid="demo-staged-building"
      >
        <div className="relative w-16 h-16 mb-6">
          <div className="absolute inset-0 border-[4px] border-[color:var(--ink)] border-t-transparent rounded-full animate-spin" />
        </div>
        <div className="display text-xl font-extrabold text-[color:var(--ink)]">
          Building your board…
        </div>
      </div>
    );
  }

  // -------------------- BOARD --------------------
  return (
    <div className={embedded ? "" : "min-h-screen bg-[color:var(--paper)]"} data-testid="demo-staged-board">
      <header className={`px-4 sm:px-6 py-3 border-b-[3px] border-[color:var(--ink)] bg-[color:var(--cream)] flex items-center gap-3 flex-wrap ${embedded ? "rounded-t-2xl border-x-[3px] border-t-[3px]" : ""}`}>
        <div className="display text-lg sm:text-xl font-extrabold text-[color:var(--ink)]">
          Your starter board
        </div>
        <div className="flex items-center gap-1 text-xs text-[color:var(--ink-soft)]">
          <Volume2 size={14} /> Tap a tile to hear it
        </div>
        <div className="flex-1" />
        {!embedded && (
          <Link to="/register" className="pill-btn gold" data-testid="demo-staged-signup">
            Make this real — sign up
          </Link>
        )}
      </header>

      <main className={embedded ? "px-3 sm:px-6 py-4 border-x-[3px] border-b-[3px] border-[color:var(--ink)] rounded-b-2xl bg-[color:var(--paper)]" : "max-w-6xl mx-auto px-3 sm:px-6 py-4"}>
        {grouped.map((group) => (
          <section key={group.key} className="mb-6" data-testid={`demo-staged-category-${group.key}`}>
            <div className="flex items-center gap-2 mb-2">
              <span
                aria-hidden
                className="inline-block w-3 h-6 rounded-sm border-[2px] border-[color:var(--ink)]"
                style={{ background: group.meta.color }}
              />
              <h2 className="display text-base font-bold text-[color:var(--ink)]">
                {group.meta.label}
              </h2>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              {group.tiles.map((tile) => (
                <button
                  key={tile.slug}
                  type="button"
                  onClick={() => onTileTap(tile)}
                  className={`tile-btn text-left overflow-hidden ${pressedSlug === tile.slug ? "is-pressed" : ""}`}
                  data-testid={`demo-staged-tile-${tile.slug}`}
                  aria-label={`Speak ${tile.label}`}
                  style={{ background: "#FFFFFF", padding: 0 }}
                >
                  {/* Category color strip on the top edge — LAW §4 */}
                  <div
                    aria-hidden
                    className="h-2 w-full border-b-[3px] border-[color:var(--ink)]"
                    style={{ background: group.meta.color }}
                  />
                  <div className="p-2 flex flex-col items-center gap-1 w-full">
                    <img
                      src={tile.image}
                      alt=""
                      className="w-full aspect-square object-cover rounded-md border-[2px] border-[color:var(--ink)]"
                      loading="lazy"
                      draggable={false}
                    />
                    <div className="display font-bold text-sm text-[color:var(--ink)] text-center leading-tight">
                      {tile.label}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        ))}

        {/* Honest single-line footnote — spec §6 */}
        <div
          className="brutal-card bg-[color:var(--gold)] p-3 mt-4 text-center text-sm font-semibold text-[color:var(--ink)]"
          data-testid="demo-staged-honest-line"
        >
          In the app, these become YOUR child's real photos and voice.
        </div>

        {/* Attribution — required by ARASAAC (CC BY-NC-SA) + good practice for Openverse */}
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => setShowCredits((v) => !v)}
            className="text-xs text-[color:var(--ink-soft)] underline hover:no-underline"
            data-testid="demo-staged-credits-toggle"
          >
            {showCredits ? "Hide" : "Show"} image credits
          </button>
          {showCredits && (
            <ul
              className="mt-3 text-[11px] text-[color:var(--ink-soft)] leading-snug space-y-1 max-w-2xl mx-auto text-left"
              data-testid="demo-staged-credits-list"
            >
              {tiles.map((t) => (
                <li key={t.slug}>
                  <strong className="text-[color:var(--ink)]">{t.label}</strong>{" "}
                  — {t.attribution}
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
